import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { useServices, useSession, type HyloServices, type Id } from '@/model';
import type { PresenceInfo } from './public';
import { isRecord } from './state';
const approximate = new Set(['recently', 'within_week', 'within_month', 'long_ago']);
/** A single reader per services/login, shared by People, Chat and Chatinfo. Never opens a socket. */
export class PresenceReader {
  private values = new Map<Id, PresenceInfo>();
  private watches = new Map<Id, number>();
  private listeners = new Set<() => void>();
  private revisions = new Map<Id, number>();
  private generation = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private controllers = new Set<AbortController>();
  private cleanups: (() => void)[] = [];
  private owner: string | null;
  private login: string | undefined;
  constructor(private readonly services: HyloServices, private readonly exact: boolean) {
    const session = services.session.state.get();
    this.owner = session.phase === 'signedIn' ? session.accountId : null;
  }
  private connect() {
    if (this.cleanups.length) return;
    const services = this.services,
      current = services.session.state.get();
    this.owner = current.phase === 'signedIn' ? current.accountId : null;
    this.login = services.session.lifetime.get()?.loginId;
    this.cleanups.push(services.session.lifetime.subscribe(() => {
      const lifetime = services.session.lifetime.get();
      if (this.login !== lifetime?.loginId) {
        this.login = lifetime?.loginId;
        this.owner = lifetime?.accountId ?? null;
        this.clear();
        this.schedule();
      }
    }));
    if (this.exact) this.cleanups.push(services.sync.onSignal(['presence'], signal => this.signal(signal)));
    this.cleanups.push(services.session.state.subscribe(() => {
      const current = services.session.state.get();
      const owner = current.phase === 'signedIn' ? current.accountId : null;
      if (this.owner !== owner) {
        this.owner = owner;
        this.clear();
      }
    }));
    this.cleanups.push(services.sync.onUpdate(['me.updated', 'contact.notes.changed', 'block.changed', 'contact.removed', 'contact.added', 'account.updated'], () => {
      this.clear();
      this.schedule();
    }));
    this.cleanups.push(services.sync.connection.subscribe(() => {
      this.clear();
      if (services.sync.connection.get().phase === 'online') this.schedule();
    }));
    if (typeof window !== 'undefined') {
      const focus = () => {
        this.clear();
        if (document.visibilityState === 'visible') this.schedule();
      };
      window.addEventListener('focus', focus);
      document.addEventListener('visibilitychange', focus);
      this.cleanups.push(() => {
        window.removeEventListener('focus', focus);
        document.removeEventListener('visibilitychange', focus);
      });
    }
  }
  get = (): ReadonlyMap<Id, PresenceInfo> => this.values;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  private publish(values: Map<Id, PresenceInfo>) {
    this.values = values;
    for (const listener of this.listeners) listener();
  }
  private clear() {
    this.generation++;
    for (const c of this.controllers) c.abort();
    this.controllers.clear();
    this.publish(new Map());
  }
  private schedule() {
    if (!this.timer && this.owner && this.watches.size && (typeof document === 'undefined' || document.visibilityState !== 'hidden')) this.timer = setTimeout(() => {
      this.timer = undefined;
      void this.refresh();
    }, 50);
  }
  watch(ids: readonly Id[]) {
    if (ids.length) this.connect();
    const unique = [...new Set(ids)].filter(id => id !== this.owner);
    for (const id of unique) this.watches.set(id, (this.watches.get(id) ?? 0) + 1);
    this.schedule();
    let active = true;
    return () => {
      if (!active) return;
      active = false;
      const values = new Map(this.values);
      for (const id of unique) {
        const n = (this.watches.get(id) ?? 1) - 1;
        if (n) this.watches.set(id, n);else {
          this.watches.delete(id);
          values.delete(id);
        }
      }
      this.publish(values);
      if (!this.watches.size) {
        if (this.timer) clearTimeout(this.timer);
        this.timer = undefined;
        this.clear();
        for (const off of this.cleanups.splice(0)) off();
        this.revisions.clear();
      }
    };
  }
  signal(value: Record<string, unknown>) {
    if (value.type !== 'presence' || typeof value.account_id !== 'string' || !this.watches.has(value.account_id) || !this.owner) return;
    const id = value.account_id;
    this.revisions.set(id, (this.revisions.get(id) ?? 0) + 1);
    const values = new Map(this.values);
    const parsed = this.parse(value);
    if (parsed) values.set(id, parsed);else values.delete(id);
    this.publish(values);
  }
  private parse(value: Record<string, unknown>): PresenceInfo | undefined {
    const id = value.account_id;
    if (typeof id !== 'string' || this.services.people.blocked().get().includes(id)) return;
    const raw = value.state;
    if (raw === 'hidden') return;
    if (raw === 'online' || raw === 'offline') {
      if (!this.exact || this.services.sync.connection.get().phase !== 'online') return;
      return {
        accountId: id,
        state: raw,
        lastSeenAt: raw === 'offline' && typeof value.last_seen_at === 'string' ? value.last_seen_at : null
      };
    }
    return {
      accountId: id,
      state: approximate.has(String(raw)) ? raw as PresenceInfo['state'] : 'unknown',
      lastSeenAt: null
    };
  }
  async refresh() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
    const owner = this.owner,
      generation = this.generation;
    if (!owner) return;
    const ids = [...this.watches.keys()].filter(id => !this.services.people.blocked().get().includes(id));
    for (let i = 0; i < ids.length; i += 200) {
      const batch = ids.slice(i, i + 200),
        versions = new Map(batch.map(id => [id, this.revisions.get(id) ?? 0]));
      const controller = new AbortController();
      this.controllers.add(controller);
      try {
        const response = await this.services.http.get<{
          presence: unknown[];
        }>('/v1/presence', {
          query: {
            ids: batch.join(',')
          },
          cause: 'open',
          signal: controller.signal
        });
        if (generation !== this.generation || owner !== this.owner || controller.signal.aborted) return;
        const values = new Map(this.values);
        const rows = Array.isArray(response.presence) ? response.presence.filter(isRecord) : [];
        for (const id of batch) {
          if (!this.watches.has(id) || versions.get(id) !== (this.revisions.get(id) ?? 0)) continue;
          const row = rows.find(x => x.account_id === id);
          const status = row ? this.parse(row) : undefined;
          if (status) values.set(id, status);else values.delete(id);
        }
        this.publish(values);
      } catch {/* Presence is optional. Failure never becomes an exact/online claim. */} finally {
        this.controllers.delete(controller);
      }
    }
  }
  dispose() {
    if (this.timer) clearTimeout(this.timer);
    for (const off of this.cleanups) off();
    this.cleanups = [];
    this.watches.clear();
    this.clear();
  }
}
const readers = new WeakMap<HyloServices, PresenceReader>();
function readerFor(services: HyloServices) {
  let reader = readers.get(services);
  if (!reader) {
    reader = new PresenceReader(services, true);
    readers.set(services, reader);
  }
  return reader;
}
export function usePeoplePresence(accountIds: readonly Id[]): ReadonlyMap<Id, PresenceInfo> {
  const services = useServices(),
    session = useSession();
  const reader = useMemo(() => readerFor(services), [services]);
  const key = [...new Set(accountIds)].sort().join(',');
  useEffect(() => reader.watch(key ? key.split(',') : []), [reader, key, session]);
  const snapshot = useSyncExternalStore(reader.subscribe, reader.get, reader.get);
  return useMemo(() => new Map([...snapshot].filter(([id]) => key.split(',').includes(id))), [snapshot, key]);
}
